import type {
  OmsDrillBucket,
  OmsExecutionOverview,
  OmsLayerKpiHealth,
  OmsOverviewResponse,
  OmsResourceRow,
  OmsResourcesOverview,
  WeeklyProgressResponse,
  YearlyPerformanceAnalysis,
} from '../types';

/* ============================================================
 * Operational Health Score
 * ============================================================ */

export interface HealthSubscore {
  key: 'execution' | 'schedule' | 'workforce' | 'pipeline';
  label: string;
  /** 0–100 */
  score: number;
  detail: string;
}

export interface OperationalHealth {
  /** Weighted 0–100 composite. */
  score: number;
  health: OmsLayerKpiHealth;
  label: string;
  subscores: HealthSubscore[];
}

const clamp = (n: number, min = 0, max = 100) => Math.min(max, Math.max(min, n));
const pct = (num: number, den: number) => (den > 0 ? (num / den) * 100 : 0);

function healthOf(score: number): { health: OmsLayerKpiHealth; label: string } {
  if (score >= 75) return { health: 'good', label: 'Healthy' };
  if (score >= 50) return { health: 'attention', label: 'Needs attention' };
  return { health: 'critical', label: 'Critical' };
}

/**
 * Composite operational health from the overview payload.
 * Weights: execution quality 40%, schedule pressure 25%,
 * workforce activity 20%, pipeline activation 15%.
 * Slices that are null are excluded and weights renormalised.
 */
export function computeOperationalHealth(data: OmsOverviewResponse | null | undefined): OperationalHealth {
  const subscores: Array<HealthSubscore & { weight: number }> = [];

  const totals = data?.execution?.totals;
  if (totals) {
    const done = totals.passTasks + totals.failTasks;
    const tracked = done + totals.holdTasks + totals.dueTasks + totals.inProgressTasks;
    const passRate = done > 0 ? pct(totals.passTasks, done) : 100;
    subscores.push({
      key: 'execution',
      label: 'Execution quality',
      score: clamp(passRate),
      detail: `${totals.passTasks} pass / ${totals.failTasks} fail`,
      weight: 0.4,
    });
    const duePressure = tracked > 0 ? pct(totals.dueTasks + totals.holdTasks, tracked) : 0;
    subscores.push({
      key: 'schedule',
      label: 'Schedule pressure',
      score: clamp(100 - duePressure * 2),
      detail: `${totals.dueTasks} due · ${totals.holdTasks} on hold`,
      weight: 0.25,
    });
  }

  const resourceKpis = data?.resources?.kpis;
  if (resourceKpis && resourceKpis.length > 0) {
    const total = resourceKpis.find((k) => k.bucket === 'omsResourceTotal')?.value ?? 0;
    const working = resourceKpis.find((k) => k.bucket === 'omsResourceWorking')?.value ?? 0;
    subscores.push({
      key: 'workforce',
      label: 'Workforce activity',
      score: clamp(pct(working, total)),
      detail: `${working} of ${total} engineers active`,
      weight: 0.2,
    });
  }

  const layered = data?.kpis;
  if (layered) {
    const approved = layered.layer2.approved.value;
    const active = layered.layer2.active.value;
    subscores.push({
      key: 'pipeline',
      label: 'Pipeline activation',
      score: clamp(approved > 0 ? pct(active, approved) : 100),
      detail: `${active} of ${approved} approved projects started`,
      weight: 0.15,
    });
  }

  const weightSum = subscores.reduce((s, x) => s + x.weight, 0);
  const score =
    weightSum > 0
      ? Math.round(subscores.reduce((s, x) => s + x.score * x.weight, 0) / weightSum)
      : 0;

  return {
    score,
    ...healthOf(score),
    subscores: subscores.map(({ weight: _w, ...rest }) => rest),
  };
}

/* ============================================================
 * Project portfolio — per-row stats & risk
 * ============================================================ */

export type ProjectRiskLevel = 'low' | 'medium' | 'high' | 'critical';

export interface ProjectPortfolioRow {
  projectId: string;
  code: string;
  projectName: string;
  passTasks: number;
  failTasks: number;
  holdTasks: number;
  dueTasks: number;
  inProgressTasks: number;
  totalTracked: number;
  /** Pass share of decided (pass+fail) tasks, 0–100. */
  passRate: number;
  /** fail+due+hold share of tracked, 0–100. */
  riskShare: number;
  risk: ProjectRiskLevel;
}

export function deriveProjectPortfolio(execution: OmsExecutionOverview | null): ProjectPortfolioRow[] {
  return (execution?.projects ?? []).map((row) => {
    const decided = row.passTasks + row.failTasks;
    const tracked = Math.max(row.totalTracked, 1);
    const passRate = decided > 0 ? pct(row.passTasks, decided) : 100;
    const riskShare = pct(row.failTasks + row.dueTasks + row.holdTasks, tracked);
    let risk: ProjectRiskLevel = 'low';
    if (riskShare >= 60) risk = 'critical';
    else if (riskShare >= 35) risk = 'high';
    else if (riskShare >= 15) risk = 'medium';
    return { ...row, passRate: Math.round(passRate), riskShare: Math.round(riskShare), risk };
  });
}

/* ============================================================
 * Resource workload
 * ============================================================ */

export interface ResourceLoad extends OmsResourceRow {
  totalAssigned: number;
  /** Pending share of assigned work, 0–100. */
  loadIndex: number;
  overloaded: boolean;
}

/**
 * Load index = pending share of assigned tasks. An engineer is flagged
 * overloaded when their pending count sits in the top quartile AND they
 * have more pending than completed work.
 */
export function deriveResourceLoads(resources: OmsResourcesOverview | null): ResourceLoad[] {
  const rows = resources?.resources ?? [];
  const pendings = rows.map((r) => r.totalPending).sort((a, b) => a - b);
  const p75 = pendings.length > 0 ? pendings[Math.floor(pendings.length * 0.75)] : 0;

  return rows
    .map((row) => {
      const totalAssigned = row.totalCompleted + row.totalPending;
      const loadIndex = Math.round(pct(row.totalPending, Math.max(totalAssigned, 1)));
      const overloaded =
        row.totalPending > 0 && row.totalPending >= p75 && row.totalPending > row.totalCompleted;
      return { ...row, totalAssigned, loadIndex, overloaded };
    })
    .sort((a, b) => b.totalPending - a.totalPending);
}

/* ============================================================
 * Preventive maintenance compliance
 * ============================================================ */

export interface PmComplianceItem {
  key: keyof YearlyPerformanceAnalysis;
  label: string;
  year: number | null;
  actual: number;
  planned: number;
  /** actual / planned, 0–100 (capped). */
  compliance: number;
  health: OmsLayerKpiHealth;
}

const PM_LABELS: Record<keyof YearlyPerformanceAnalysis, string> = {
  preventiveMaintenance: 'Preventive maintenance',
  moduleCleaning: 'Module cleaning',
  grassCutting: 'Grass cutting',
  totalTasks: 'All tasks',
};

/** Latest-year compliance for each maintenance category (skips totalTasks). */
export function derivePmCompliance(yearly: YearlyPerformanceAnalysis | null): PmComplianceItem[] {
  if (!yearly) return [];
  const keys: Array<keyof YearlyPerformanceAnalysis> = [
    'preventiveMaintenance',
    'moduleCleaning',
    'grassCutting',
  ];
  return keys.map((key) => {
    const series = yearly[key] ?? [];
    const latest = series.length > 0 ? series[series.length - 1] : null;
    const actual = latest?.actual ?? 0;
    const planned = latest?.planned ?? 0;
    const compliance = planned > 0 ? Math.round(clamp(pct(actual, planned))) : 0;
    const { health } = healthOf(compliance);
    return {
      key,
      label: PM_LABELS[key],
      year: latest?.year ?? null,
      actual,
      planned,
      compliance,
      health,
    };
  });
}

/* ============================================================
 * Momentum (week-over-week)
 * ============================================================ */

export interface Momentum {
  /** Latest week completion rate, 0–100. */
  current: number;
  /** Percentage-point change vs previous week (null with <2 weeks). */
  delta: number | null;
  spark: number[];
}

export function deriveMomentum(weekly: WeeklyProgressResponse | null): Momentum {
  const series = weekly?.series ?? [];
  const rates = series.map((p) => p.completionRate);
  const current = rates.length > 0 ? Math.round(rates[rates.length - 1]) : 0;
  const delta =
    rates.length >= 2 ? Math.round(rates[rates.length - 1] - rates[rates.length - 2]) : null;
  return { current, delta, spark: rates };
}

/* ============================================================
 * Executive insights
 * ============================================================ */

export type InsightTone = 'positive' | 'warning' | 'critical' | 'neutral';

export interface ExecutiveInsight {
  id: string;
  tone: InsightTone;
  eyebrow: string;
  title: string;
  detail: string;
  metric?: string;
  projectId?: string;
  bucket?: OmsDrillBucket;
}

/**
 * Deterministic, data-derived insight feed answering "where should
 * management focus first?" — ordered critical → warning → positive.
 *
 * Problem cards (needs attention, most delayed) record their projects in
 * `flaggedProjectIds`; the top-performer card excludes those, so a project
 * can never be celebrated and flagged as a problem at the same time. Two
 * problem cards may still share a project — that reads as consistent, not
 * contradictory.
 */
export function deriveExecutiveInsights(data: OmsOverviewResponse | null | undefined): ExecutiveInsight[] {
  if (!data) return [];
  const insights: ExecutiveInsight[] = [];
  const portfolio = deriveProjectPortfolio(data.execution ?? null);
  const loads = deriveResourceLoads(data.resources ?? null);
  const flaggedProjectIds = new Set<string>();

  const riskiest = portfolio
    .filter((p) => p.totalTracked >= 3 && (p.risk === 'critical' || p.risk === 'high'))
    .sort((a, b) => b.riskShare - a.riskShare)[0];
  if (riskiest) {
    flaggedProjectIds.add(riskiest.projectId);
    insights.push({
      id: 'riskiest-project',
      tone: riskiest.risk === 'critical' ? 'critical' : 'warning',
      eyebrow: 'Needs attention',
      title: riskiest.projectName,
      detail: `${riskiest.failTasks} failed, ${riskiest.dueTasks} due and ${riskiest.holdTasks} on hold out of ${riskiest.totalTracked} tracked tasks.`,
      metric: `${riskiest.riskShare}% at risk`,
      projectId: riskiest.projectId,
    });
  }

  const delayed = (data.resources?.startedProjects ?? [])
    .filter((p) => p.overdueTasks > 0)
    .sort((a, b) => b.overdueTasks - a.overdueTasks)[0];
  if (delayed) {
    flaggedProjectIds.add(delayed.projectId);
    insights.push({
      id: 'most-delayed',
      tone: 'critical',
      eyebrow: 'Most delayed',
      title: delayed.projectName,
      detail: `${delayed.overdueTasks} overdue of ${delayed.totalTasks} tasks with ${delayed.assignedResourceCount} engineers assigned.`,
      metric: `${delayed.overdueTasks} overdue`,
      projectId: delayed.projectId,
    });
  }

  const overloaded = loads.find((r) => r.overloaded);
  if (overloaded) {
    insights.push({
      id: 'overloaded-engineer',
      tone: 'warning',
      eyebrow: 'Workload alert',
      title: overloaded.resourceName,
      detail: `${overloaded.totalPending} pending vs ${overloaded.totalCompleted} completed across ${overloaded.projects.length} project${overloaded.projects.length === 1 ? '' : 's'}. Consider rebalancing.`,
      metric: `${overloaded.loadIndex}% load`,
      bucket: 'omsResourceWorking',
    });
  }

  const idle = loads.filter((r) => !r.isWorking).length;
  if (idle > 0) {
    insights.push({
      id: 'idle-resources',
      tone: 'warning',
      eyebrow: 'Idle capacity',
      title: `${idle} engineer${idle === 1 ? '' : 's'} inactive`,
      detail: `No submissions within the inactivity threshold (${data.resources?.thresholds.inactiveDays ?? '—'} days). Reassign or follow up.`,
      bucket: 'omsResourceInactive',
    });
  }

  const readyToSendReview = data.kpis?.layer2.readyToSendReview;
  if (readyToSendReview && readyToSendReview.value > 0) {
    insights.push({
      id: 'ready-to-send-review',
      tone: 'neutral',
      eyebrow: 'Awaiting review submission',
      title: `${readyToSendReview.value} draft project${readyToSendReview.value === 1 ? '' : 's'} ready to send for review`,
      detail: 'Assets, equipment and resources are attached — send for review to move them into approval.',
      bucket: 'omsReadyToSendReview',
    });
  }

  const pm = derivePmCompliance(data.trends.yearly ?? null)
    .filter((c) => c.planned > 0)
    .sort((a, b) => a.compliance - b.compliance)[0];
  if (pm && pm.compliance < 75) {
    insights.push({
      id: 'pm-compliance',
      tone: pm.compliance < 50 ? 'critical' : 'warning',
      eyebrow: 'Maintenance gap',
      title: `${pm.label} at ${pm.compliance}%`,
      detail: `${pm.actual} of ${pm.planned} planned activities completed${pm.year ? ` in ${pm.year}` : ''}.`,
      metric: `${pm.compliance}% compliance`,
    });
  }

  // Top performer must be genuinely healthy end-to-end: low share of work
  // at risk (fail/due/hold), a meaningful number of decided tasks, a strong
  // pass rate, and not already flagged by a problem card above. A project
  // with a perfect pass rate but a huge overdue backlog never qualifies.
  const overdueByProject = new Map(
    (data.resources?.startedProjects ?? []).map((p) => [p.projectId, p.overdueTasks])
  );
  const best = portfolio
    .filter((p) =>
      p.totalTracked >= 3 &&
      p.passTasks + p.failTasks >= 3 &&
      p.risk === 'low' &&
      p.passRate >= 70 &&
      (overdueByProject.get(p.projectId) ?? 0) === 0 &&
      !flaggedProjectIds.has(p.projectId)
    )
    .sort((a, b) => b.passRate - a.passRate || a.riskShare - b.riskShare || b.totalTracked - a.totalTracked)[0];
  if (best) {
    insights.push({
      id: 'best-project',
      tone: 'positive',
      eyebrow: 'Top performer',
      title: best.projectName,
      detail: `${best.passTasks} of ${best.passTasks + best.failTasks} decided tasks passed, nothing overdue and only ${best.riskShare}% of tracked work at risk.`,
      metric: `${best.passRate}% pass`,
      projectId: best.projectId,
    });
  }

  const momentum = deriveMomentum(data.trends.weekly ?? null);
  if (momentum.delta !== null && momentum.delta !== 0) {
    insights.push({
      id: 'momentum',
      tone: momentum.delta > 0 ? 'positive' : 'warning',
      eyebrow: 'Productivity trend',
      title: momentum.delta > 0 ? 'Completion rate climbing' : 'Completion rate slipping',
      detail: `Weekly completion rate moved ${momentum.delta > 0 ? 'up' : 'down'} ${Math.abs(momentum.delta)} pts to ${momentum.current}% this week.`,
      metric: `${momentum.delta > 0 ? '+' : ''}${momentum.delta} pts`,
    });
  }

  const toneRank: Record<InsightTone, number> = { critical: 0, warning: 1, neutral: 2, positive: 3 };
  return insights.sort((a, b) => toneRank[a.tone] - toneRank[b.tone]);
}
